/**
 * Button Component - Reusable button with consistent styling
 * Supports different variants, sizes, and permission gating
 */

import { useTheme } from '../contexts/ThemeContext';
import { PermissionGate } from './permissions/PermissionGate';

const Button = ({
  children,
  variant = 'primary', // 'primary', 'secondary', 'outline', 'view', 'edit', 'delete', 'save', 'cancel', 'warning', 'ghost'
  size = 'md', // 'sm', 'md', 'lg'
  appearance = 'dark', // 'dark', 'light'
  href,
  to, // For React Router Link support
  onClick,
  className = '',
  style = {},
  disabled = false,
  loading = false,
  fullWidth = false,
  icon, // Optional icon to display before text
  type = 'button', // button type attribute

  // Permission props
  permission,
  permissions,
  role,
  roles,
  requireAll = false,
  fallback = null,
  showDisabledWhenNoPermission = false
}) => {
  const { theme } = useTheme();

  const getVariantStyles = () => {
    switch (variant) {
      // Action variants - use theme action colors
      case 'view':
        return {
          background: theme.colors.actionView,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'edit':
        return {
          background: theme.colors.actionEdit,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'delete':
      case 'deactivate':
      case 'danger': // standardizing
        return {
          background: theme.colors.actionDelete,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'save':
      case 'submit':
      case 'success': // standardizing
        return {
          background: theme.colors.actionSave,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'cancel':
        return {
          background: theme.colors.actionCancel,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'warning':
        return {
          background: theme.colors.actionWarning,
          color: 'white',
          border: 'none',
          backdropFilter: 'blur(4px)'
        };
      case 'ghost':
        return {
          background: 'transparent',
          color: theme.colors.textPrimary,
          border: 'none',
          boxShadow: 'none'
        };
      // Original variants
      case 'secondary':
        return {
          background: theme.colors.surface,
          color: theme.colors.primary,
          border: `1px solid ${theme.colors.border}`,
          backdropFilter: 'blur(8px)'
        };
      case 'outline':
        if (appearance === 'light') {
          return {
            background: 'rgba(255, 255, 255, 0.05)',
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.primary}99`,
            backdropFilter: 'blur(4px)'
          };
        }
        return {
          background: 'rgba(0, 0, 0, 0.2)',
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.primary}80`,
          backdropFilter: 'blur(4px)'
        };
      case 'glass':
        return {
          background: theme.colors.glass.background,
          borderColor: theme.colors.glass.border,
          color: theme.colors.glass.text,
          borderWidth: '1px',
          borderStyle: 'solid',
          backdropFilter: `blur(${theme.colors.glass.blur})`,
          boxShadow: theme.shadows.sm
        };
      default: // primary
        return {
          background: `rgba(0, 255, 148, var(--button-opacity-primary, 0.85))`, // Uses CSS variable controlled by theme context
          // Fallback to theme color if needed but using CSS var for opacity toggle
          color: theme.colors.background,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: `0 0 15px ${theme.colors.primary}40`,
          backdropFilter: `blur(var(--button-blur, 8px))`
        };
    }
  };

  const getHoverColor = () => {
    switch (variant) {
      case 'view':
        return theme.colors.actionViewHover;
      case 'edit':
        return theme.colors.actionEditHover;
      case 'delete':
      case 'deactivate':
      case 'danger':
        return theme.colors.actionDeleteHover;
      case 'save':
      case 'submit':
      case 'success':
        return theme.colors.actionSaveHover;
      case 'cancel':
        return theme.colors.actionCancelHover;
      case 'warning':
        return theme.colors.actionWarningHover;
      case 'ghost':
        return theme.colors.glassLow?.background || 'rgba(255,255,255,0.1)';
      case 'glass':
        return theme.colors.glassHigh?.background || 'rgba(255,255,255,0.2)';
      case 'secondary':
        return theme.colors.surfaceLight;
      case 'primary':
        return theme.colors.primaryDark;
      default:
        return null;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          fontSize: theme.typography.sizes.sm
        };
      case 'lg':
        return {
          padding: `${theme.spacing.lg} ${theme.spacing['2xl']}`,
          fontSize: theme.typography.sizes.lg
        };
      default: // md
        return {
          padding: `${theme.spacing.md} ${theme.spacing.xl}`,
          fontSize: theme.typography.sizes.base
        };
    }
  };

  const baseStyles = {
    borderRadius: theme.borderRadius.md,
    textDecoration: 'none',
    fontWeight: theme.typography.weights.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: theme.transitions.normal,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
    opacity: (disabled || loading) ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
    textAlign: 'center',
    boxShadow: variant === 'ghost' ? 'none' : theme.shadows.sm,
    ...getVariantStyles(),
    ...getSizeStyles(),
    ...style
  };

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  const handleMouseEnter = (e) => {
    const hoverColor = getHoverColor();
    if (hoverColor && !disabled && !loading) {
      e.currentTarget.style.backgroundColor = hoverColor;
      if (variant !== 'ghost') {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = theme.shadows.md;
      }
    }
  };

  const handleMouseLeave = (e) => {
    if (!disabled && !loading) {
      const variantStyles = getVariantStyles();
      e.currentTarget.style.backgroundColor = variantStyles.background;
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = variant === 'ghost' ? 'none' : theme.shadows.sm;
    }
  };

  const spinnerStyles = {
    animation: 'spin 1s linear infinite',
    display: 'inline-block'
  };

  const contentStyles = {
    opacity: loading ? 0.7 : 1
  };

  const buttonContent = (
    <>
      {loading && <span style={spinnerStyles} aria-hidden="true">⟳</span>}
      {icon && !loading && <span aria-hidden="true">{icon}</span>}
      <span style={contentStyles}>
        {children}
      </span>
    </>
  );

  const renderButton = () => {
    // Support for React Router Link (if 'to' prop is provided)
    if (to && !disabled) {
      // Dynamically import Link to avoid dependency issues with react-router-dom if not installed (though it should be)
      const Link = require('react-router-dom').Link;
      return (
        <Link
          to={to}
          className={`btn btn-${variant} btn-${size} ${className}`}
          style={baseStyles}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {buttonContent}
        </Link>
      );
    }

    // Support for external links
    if (href && !disabled) {
      return (
        <a
          href={href}
          className={`btn btn-${variant} btn-${size} ${className}`}
          style={baseStyles}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {buttonContent}
        </a>
      );
    }

    // Default button element
    return (
      <button
        type={type}
        className={`btn btn-${variant} btn-${size} ${className}`}
        style={baseStyles}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disabled={disabled || loading}
        aria-busy={loading}
      >
        {buttonContent}
      </button>
    );
  };

  // If no permissions specified, render button without permission check
  if (!permission && !permissions && !role && !roles) {
    return renderButton();
  }

  // Permission-protected button
  const disabledButton = (
    <button
      className={`btn btn-${variant} btn-${size} ${className}`}
      style={{ ...baseStyles, opacity: 0.5, cursor: 'not-allowed' }}
      disabled={true}
      title="You don't have permission to perform this action"
    >
      {buttonContent}
    </button>
  );

  return (
    <PermissionGate
      permission={permission}
      permissions={permissions}
      role={role}
      roles={roles}
      requireAll={requireAll}
      fallback={showDisabledWhenNoPermission ? disabledButton : fallback}
    >
      {renderButton()}
    </PermissionGate>
  );
};

export default Button;
